import { createContext, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router";

type ResellerContextType = {
	resellerId: number | null;
	setReseller: (id: number | null) => void;
};

const ResellerContext = createContext<ResellerContextType | null>(null);

export function ResellerProvider({ children }: { children: React.ReactNode }) {
	const [searchParams, setSearchParams] = useSearchParams();

	const resellerId = useMemo(() => {
		const raw = searchParams.get("reseller");
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, [searchParams]);

	const setReseller = useCallback(
		(id: number | null) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (id === null) {
						next.delete("reseller");
					} else {
						next.set("reseller", String(id));
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	return (
		<ResellerContext.Provider value={{ resellerId, setReseller }}>
			{children}
		</ResellerContext.Provider>
	);
}

export function useReseller() {
	const context = useContext(ResellerContext);
	if (!context) {
		throw new Error("useReseller must be used within a ResellerProvider");
	}
	return context;
}
